/**
 * Canonical South African Rand formatters.
 *
 * SINGLE SOURCE OF TRUTH for currency rendering across the web frontend
 * (CUX-1, 2026-05-21). The codebase previously had FOUR un-unified
 * formatters — `formatZarCents` (lib/api/dashboard.ts), `formatZAR`
 * (lib/api/distributions.ts), a second local `formatZarCents`
 * (routes/admin/billing-screen.tsx), and the `fmtR`/`formatRand` family
 * here — each with subtle drift (period vs comma decimal, dropped cents
 * on round values, regular-space vs NBSP vs U+202F separators). A single
 * trust could render the same money two different ways on two screens.
 *
 * The canonical SA fiduciary format is:
 *
 *     R 123 456,78
 *
 *   - "R" prefix, then a no-break space (U+00A0 - see the NNBSP note below,
 *     which is the authority; U+202F was the original choice and is NOT used)
 *   - U+00A0 (no-break space) as the thousands separator
 *   - "," (comma) as the decimal separator
 *   - always two decimal places
 *
 * IMPORTANT — ICU drift: en-ZA `toLocaleString` in this project's Node 20
 * ICU emits an ASCII comma for thousands and an ASCII period for the
 * decimal ("1,542,380.42"), NOT the SA-correct separators. So we never
 * trust the locale's separators directly: we group the integer part and
 * compose the decimal ourselves, which is immune to ICU build drift.
 *
 * `formatZarCents` is the primitive. `fmtR` / `formatRand` /
 * `formatRandFromCents` are kept as thin wrappers for the existing
 * call-sites (the hero renders its "R" + cents superscript separately,
 * so it needs the number-only `fmtR`).
 *
 * Examples:
 *   formatZarCents(15423804200)            — "R 154 238 042,00"
 *   formatZarCents(34275104200)            — "R 342 751 042,00"
 *   formatZarCents(-245000000)             — "-R 2 450 000,00"
 *   fmtR(3427510)                          — "3 427 510"
 *   fmtR(3427510.42, { cents: true })      — "3 427 510,42"
 *   formatRand(3427510)                    — "R 3 427 510"
 *   formatRand(3427510.42, { cents: true })— "R 3 427 510,42"
 *   formatRandFromCents(342751042)         — "R 3 427 510,42"
 */

/**
 * The SA thousands separator, and the glue between "R" and the number.
 *
 * U+00A0 (no-break space), NOT U+202F (narrow no-break space), since
 * 2026-08-25.
 *
 * WHY THE CHANGE. NEXT.md's open item B11 asked Paul to "confirm the 1 150 000
 * narrow-no-break-space reads grouped on prod (swap to plain space if tight)".
 * His prod walkthrough answered it. At 4x zoom on the recording, the client
 * dashboard's split line reads "R1150000": in Inter at 11.5px, U+202F collapses
 * to very nearly zero width, so a seven-figure number renders as one unbroken
 * digit run. Two panels above it, the same value formatted by the same constant
 * reads "R 1 150 000,00", because the serif display face renders U+202F wide
 * enough to see. One separator, two fonts, two different answers, and the small
 * one is where the number actually needs help being read.
 *
 * U+00A0 is a normal-width space that still refuses to break the number across
 * a line, which is the only property U+202F was chosen for. The name stays
 * `NNBSP` because five call-sites import it and a rename buys nothing.
 */
export const NNBSP = ' ';

/**
 * Group an integer's digits with the SA thousands separator (`NNBSP`).
 * Built from `toLocaleString('en-ZA')` with the locale separators normalised to
 * that one character, so the result is independent of ICU drift.
 */
function groupThousands(intAbs: number): string {
  return intAbs.toLocaleString('en-ZA', { useGrouping: true }).replace(/[^0-9]/g, NNBSP);
}

/**
 * CANONICAL currency formatter. Integer ZAR cents → "R 123 456,78".
 *
 * Uses the U+00A0 thousands separator, comma decimal, always 2dp, and a
 * leading "-" for negative (reversal) amounts. This is the one helper
 * every currency render in the frontend should route through.
 */
export function formatZarCents(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(Math.round(cents));
  const rands = Math.floor(abs / 100);
  const remainder = abs % 100;
  const rPart = groupThousands(rands);
  const cPart = remainder.toString().padStart(2, '0');
  return `${sign}R${NNBSP}${rPart},${cPart}`;
}

/**
 * Number-portion-only formatter (no "R" prefix). For call-sites that
 * render the currency symbol separately — e.g. the hero's
 * `<span className="currency">R</span>{fmtR(value)}` superscript pattern.
 *
 * Default: integer grouping, no decimal. With `{ cents: true }`: two
 * decimal places using the canonical comma decimal separator.
 *
 * Negatives are rendered as their absolute value (callers that need a
 * sign render it themselves) — preserves prior `fmtR` behaviour.
 */
export function fmtR(v: number, opts: { cents?: boolean } = {}): string {
  if (opts.cents) {
    const abs = Math.abs(v);
    const intPart = Math.floor(abs);
    const cPart = Math.round((abs - intPart) * 100)
      .toString()
      .padStart(2, '0');
    return `${groupThousands(intPart)},${cPart}`;
  }
  return groupThousands(Math.round(Math.abs(v)));
}

/** Format a major-unit Rand amount as "R 1 234 567" (or "R 1 234 567,89" with cents). */
export function formatRand(v: number, opts: { cents?: boolean } = {}): string {
  return `R${NNBSP}${fmtR(v, opts)}`;
}

/**
 * Format a cents-denominated amount as Rand. Defaults to showing cents
 * (2dp). For the canonical always-2dp behaviour prefer `formatZarCents`,
 * which this delegates to when cents are shown.
 */
export function formatRandFromCents(cents: number, opts: { cents?: boolean } = {}): string {
  const showCents = opts.cents ?? true;
  if (showCents) return formatZarCents(cents);
  return formatRand(cents / 100);
}

/**
 * Group a whole-Rand digit string with SPACE separators for an EDITABLE input
 * (e.g. "1150000" → "1 150 000"). Unlike `fmtR` (display-only, U+00A0 no-break
 * no-break separators), this uses a REGULAR ASCII space so a user can type and
 * delete naturally in a text field, and `stripRandGrouping` is its exact
 * inverse. Non-digit noise (a pasted "R", commas) is dropped before grouping;
 * an empty result returns ''.
 *
 * Used by the onboarding asset-value intake so Paul stops "counting zeros" — the
 * STORED value stays a plain number string (`stripRandGrouping` on change), only
 * the displayed/typed representation is grouped.
 */
export function groupRandInput(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits === '') return '';
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/**
 * Strip `groupRandInput`'s space grouping back to bare digits ("1 150 000" →
 * "1150000"), so the stored intake value is always a plain number string the
 * confirm path's `/^\d+$/` gate accepts.
 */
export function stripRandGrouping(formatted: string): string {
  return formatted.replace(/\D/g, '');
}
